import type { ScormModuleStatus } from '@zeus/index'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Spinner from '~/components/Spinner'
import { Card, CardContent, CardHeader, CardTitle } from '~/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '~/components/ui/table'
import { formatSessionDate } from '~/helpers/dates/session-dates'
import { tourAnchor } from '~/helpers/tour/anchors'
import { useListSearch } from '~/hooks/use-list-search'
import { filterModules } from '~/pages/backoffice/admin/modules/filter-modules'
import { MODULE_STATUS_KEYS, MODULE_STATUS_STYLES } from '~/pages/backoffice/admin/modules/module-status'
import ModuleRowActions from '~/pages/backoffice/admin/modules/ModuleRowActions'
import { NoResults } from '~/pages/backoffice/NoResults'
import ResultsStatus from '~/pages/backoffice/ResultsStatus'
import TablePagination from '~/pages/backoffice/TablePagination'
import { FilterSelect, SearchInput, TableToolbar } from '~/pages/backoffice/TableToolbar'
import { useClientPagination } from '~/pages/backoffice/use-client-pagination'
import { formatBytes } from '~/utils/bytes'

export type ModuleRow = {
  id: string
  title: string
  scormVersion?: string | null
  sizeBytes: number
  status: ScormModuleStatus
  contentUpdatedAt?: Date | null
}

function StatusBadge({ status }: { status: ScormModuleStatus }) {
  const { t } = useTranslation()
  return (
    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${MODULE_STATUS_STYLES[status]}`}>
      {t(MODULE_STATUS_KEYS[status])}
    </span>
  )
}

/**
 * La carte « Vos modules » : recherche et filtre de statut (#107), le tableau,
 * son état de chargement et ses deux états vides — aucun module, ou aucun qui
 * réponde aux critères — paginé, 10 par défaut (#198). Sans `onDelete`,
 * `onReplace` ni `onRename`, pas de bouton correspondant : le porteur n'en a
 * pas le droit (#11, #311).
 */
export default function ModulesList({ modules, loading, onDelete, onReplace, onRename }: {
  modules: ModuleRow[]
  loading: boolean
  onDelete?: (module: ModuleRow) => void
  onReplace?: (module: ModuleRow) => void
  onRename?: (module: ModuleRow) => void
}) {
  const { t, i18n } = useTranslation()
  const { search, setSearch, query } = useListSearch()
  const [status, setStatus] = useState<ScormModuleStatus | ''>('')
  const visibleModules = useMemo(() => filterModules(modules, { query, status }), [modules, query, status])
  const { pageRows, pagination } = useClientPagination(visibleModules, `${query}|${status}`)

  const reset = () => {
    setSearch('')
    setStatus('')
  }

  return (
    <Card {...tourAnchor('modules.list')}>
      <CardHeader className="gap-4">
        <CardTitle>{t('modules.list.title')}</CardTitle>
        <TableToolbar>
          <SearchInput
            {...tourAnchor('modules.search')}
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <FilterSelect
            aria-label={t('common.status')}
            {...tourAnchor('modules.filters')}
            value={status}
            onChange={e => setStatus(e.target.value as ScormModuleStatus | '')}
          >
            <option value="">{t('common.allStatuses')}</option>
            {Object.entries(MODULE_STATUS_KEYS).map(([value, key]) => (
              <option key={value} value={value}>{t(key)}</option>
            ))}
          </FilterSelect>
        </TableToolbar>
        {modules.length > 0 && <ResultsStatus count={visibleModules.length} filtering={query !== '' || status !== ''} />}
      </CardHeader>
      <CardContent>
        {loading && modules.length === 0
          ? (
              <div className="flex justify-center p-6">
                <Spinner className="size-6" />
              </div>
            )
          : modules.length === 0
            ? <p className="p-4 text-center text-sm text-muted-foreground">{t('modules.empty')}</p>
            : visibleModules.length === 0
              ? <NoResults query={query} onReset={reset} />
              : (
                  <>
                    <Table aria-label={t('modules.list.title')}>
                      <TableHeader>
                        <TableRow>
                          <TableHead>{t('common.title')}</TableHead>
                          <TableHead>{t('common.version')}</TableHead>
                          <TableHead className="text-right">{t('storage.size')}</TableHead>
                          <TableHead>{t('common.status')}</TableHead>
                          <TableHead className="text-right">{t('common.action')}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {pageRows.map(m => (
                          <TableRow key={m.id}>
                            <TableCell>
                              <span className="font-medium">{m.title}</span>
                              {m.contentUpdatedAt != null && (
                                <span className="block text-xs text-muted-foreground">
                                  {t('modules.contentUpdatedAt', { date: formatSessionDate(m.contentUpdatedAt, i18n.language, undefined, { day: 'numeric', month: 'long', year: 'numeric' }) })}
                                </span>
                              )}
                            </TableCell>
                            <TableCell>{m.scormVersion ?? '—'}</TableCell>
                            {/* Un module FAILED est à zéro octet : ses fichiers ont
                            été effacés, il ne consomme rien. */}
                            <TableCell className="text-right tabular-nums text-muted-foreground">
                              {formatBytes(m.sizeBytes, i18n.language)}
                            </TableCell>
                            <TableCell><StatusBadge status={m.status} /></TableCell>
                            <TableCell className="text-right" {...tourAnchor('modules.actions')}>
                              <ModuleRowActions module={m} onReplace={onReplace} onRename={onRename} onDelete={onDelete} />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    <TablePagination {...pagination} />
                  </>
                )}
      </CardContent>
    </Card>
  )
}
